import {getInputDevicePermissionState} from '@pexip/media-control';

import type {UserMediaStatus} from './types';
import {
    createUserMediaStatus,
    DeniedDevices,
    hasInputStatus,
    InputStatusMask,
    MediaInputStatusFlag,
} from './types';

export type UserMediaValidator = (status: UserMediaStatus) => boolean;

const toPermissionFlag = (state: PermissionState): MediaInputStatusFlag => {
    switch (state) {
        case 'granted':
            return MediaInputStatusFlag.PermissionGranted;
        case 'denied':
            return MediaInputStatusFlag.PermissionDenied;
        case 'prompt':
            return MediaInputStatusFlag.PermissionPrompt;
    }
};

const hasFlag = (
    status: UserMediaStatus | undefined,
    kind: 'audio' | 'video',
    flag: MediaInputStatusFlag,
) =>
    Boolean(
        status &&
        typeof status === 'object' &&
        hasInputStatus(status, kind, flag),
    );

export const isFallbackVideo: UserMediaValidator = status =>
    hasFlag(status, 'video', MediaInputStatusFlag.Fallback);

export const isFallbackAudio: UserMediaValidator = status =>
    hasFlag(status, 'audio', MediaInputStatusFlag.Fallback);

export const isFallback: UserMediaValidator = status =>
    isFallbackAudio(status) || isFallbackVideo(status);

export const hasNoDevice: UserMediaValidator = status =>
    hasFlag(status, 'audio', MediaInputStatusFlag.NoDevice) &&
    hasFlag(status, 'video', MediaInputStatusFlag.NoDevice);

export const hasNoAudioDevices: UserMediaValidator = status =>
    hasFlag(status, 'audio', MediaInputStatusFlag.NoDevice);

export const hasNoVideoDevices: UserMediaValidator = status =>
    hasFlag(status, 'video', MediaInputStatusFlag.NoDevice);

export const areBothGranted: UserMediaValidator = status =>
    hasFlag(status, 'audio', MediaInputStatusFlag.TrackPresent) &&
    hasFlag(status, 'video', MediaInputStatusFlag.TrackPresent);

export const isGrantedVideo: UserMediaValidator = status =>
    hasFlag(status, 'video', MediaInputStatusFlag.TrackPresent);

export const isGrantedAudio: UserMediaValidator = status =>
    hasFlag(status, 'audio', MediaInputStatusFlag.TrackPresent);

export const isGranted: UserMediaValidator = status =>
    isGrantedVideo(status) || isGrantedAudio(status);

const hasInputError = (status: UserMediaStatus, kind: 'audio' | 'video') =>
    (status[kind] & InputStatusMask.Error) !== 0;

export const isOnlyAudioError: UserMediaValidator = status =>
    Boolean(
        status &&
        typeof status === 'object' &&
        hasInputError(status, 'audio') &&
        !hasInputError(status, 'video'),
    );

export const isOnlyVideoError: UserMediaValidator = status =>
    Boolean(
        status &&
        typeof status === 'object' &&
        hasInputError(status, 'video') &&
        !hasInputError(status, 'audio'),
    );

export const isRejected: UserMediaValidator = status =>
    hasFlag(status, 'audio', MediaInputStatusFlag.PermissionDenied) &&
    hasFlag(status, 'video', MediaInputStatusFlag.PermissionDenied);

export const isRejectedOnlyAudio: UserMediaValidator = status =>
    hasFlag(status, 'audio', MediaInputStatusFlag.PermissionDenied) &&
    !hasFlag(status, 'video', MediaInputStatusFlag.PermissionDenied);

export const isRejectedOnlyVideo: UserMediaValidator = status =>
    hasFlag(status, 'video', MediaInputStatusFlag.PermissionDenied) &&
    !hasFlag(status, 'audio', MediaInputStatusFlag.PermissionDenied);

export const isOverConstrained: UserMediaValidator = status =>
    hasFlag(status, 'audio', MediaInputStatusFlag.Overconstrained) ||
    hasFlag(status, 'video', MediaInputStatusFlag.Overconstrained);

export const isUnknownError: UserMediaValidator = status =>
    hasFlag(status, 'audio', MediaInputStatusFlag.UnknownError) ||
    hasFlag(status, 'video', MediaInputStatusFlag.UnknownError);

/**
 * Checks whether either requested media input timed out.
 *
 * @example
 * isTimedOut(createUserMediaStatus({audio: MediaInputStatusFlag.TimedOut}));
 */
export const isTimedOut: UserMediaValidator = status =>
    hasFlag(status, 'audio', MediaInputStatusFlag.TimedOut) ||
    hasFlag(status, 'video', MediaInputStatusFlag.TimedOut);

export const isInitial: UserMediaValidator = status =>
    Boolean(
        status &&
        typeof status === 'object' &&
        status.audio === MediaInputStatusFlag.None &&
        status.video === MediaInputStatusFlag.None,
    );

export const isInitialPermissions: UserMediaValidator = status =>
    Boolean(
        status &&
        typeof status === 'object' &&
        !hasFlag(status, 'audio', MediaInputStatusFlag.Requested) &&
        !hasFlag(status, 'video', MediaInputStatusFlag.Requested) &&
        ((status.audio & InputStatusMask.Permission) !== 0 ||
            (status.video & InputStatusMask.Permission) !== 0),
    );

export const isInitialPermissionsNotGranted: UserMediaValidator = status =>
    isInitialPermissions(status) &&
    (hasFlag(status, 'audio', MediaInputStatusFlag.PermissionPrompt) ||
        hasFlag(status, 'video', MediaInputStatusFlag.PermissionPrompt)) &&
    !hasFlag(status, 'audio', MediaInputStatusFlag.PermissionGranted) &&
    !hasFlag(status, 'video', MediaInputStatusFlag.PermissionGranted);

export const isRequestPermissionNeeded: UserMediaValidator = status =>
    isInitialPermissions(status) &&
    (hasFlag(status, 'audio', MediaInputStatusFlag.PermissionPrompt) ||
        hasFlag(status, 'video', MediaInputStatusFlag.PermissionPrompt)) &&
    !hasFlag(status, 'audio', MediaInputStatusFlag.PermissionDenied) &&
    !hasFlag(status, 'video', MediaInputStatusFlag.PermissionDenied);

export const isInitialPermissionsGranted: UserMediaValidator = status =>
    isInitialPermissions(status) &&
    (hasFlag(status, 'audio', MediaInputStatusFlag.PermissionGranted) ||
        hasFlag(status, 'video', MediaInputStatusFlag.PermissionGranted));

export const isAudioDeviceInUse: UserMediaValidator = status =>
    hasFlag(status, 'audio', MediaInputStatusFlag.DeviceInUse) &&
    !hasFlag(status, 'video', MediaInputStatusFlag.DeviceInUse);

export const isDeviceInUse: UserMediaValidator = status =>
    hasFlag(status, 'audio', MediaInputStatusFlag.DeviceInUse) ||
    hasFlag(status, 'video', MediaInputStatusFlag.DeviceInUse);

export const isDevicesInUse: UserMediaValidator = status =>
    hasFlag(status, 'audio', MediaInputStatusFlag.DeviceInUse) &&
    hasFlag(status, 'video', MediaInputStatusFlag.DeviceInUse);

export const isVideoDeviceInUse: UserMediaValidator = status =>
    hasFlag(status, 'video', MediaInputStatusFlag.DeviceInUse) &&
    !hasFlag(status, 'audio', MediaInputStatusFlag.DeviceInUse);

export const toDeniedDevices = (
    status?: UserMediaStatus,
): DeniedDevices | undefined => {
    if (!status) {
        return undefined;
    }

    if (isRejectedOnlyVideo(status)) {
        return DeniedDevices.Camera;
    }

    if (isRejectedOnlyAudio(status)) {
        return DeniedDevices.Microphone;
    }

    if (isRejected(status)) {
        return DeniedDevices.Both;
    }
};

export const getPermissionStatus = ({
    audio,
    video,
}: {
    audio: PermissionState;
    video: PermissionState;
}) =>
    createUserMediaStatus({
        audio: toPermissionFlag(audio),
        video: toPermissionFlag(video),
    });

export const deriveInitialPermissionStatus = async (
    prevStatus: UserMediaStatus,
    getPermissionState = getInputDevicePermissionState,
): Promise<UserMediaStatus> => {
    if (isInitialPermissions(prevStatus) || isRejected(prevStatus)) {
        return prevStatus;
    }
    const permission = await getPermissionState();
    const deriveFlag = (kind: 'audio' | 'video', state: PermissionState) => {
        if (hasFlag(prevStatus, kind, MediaInputStatusFlag.PermissionDenied)) {
            return MediaInputStatusFlag.PermissionDenied;
        }
        if (hasFlag(prevStatus, kind, MediaInputStatusFlag.TrackPresent)) {
            return MediaInputStatusFlag.PermissionGranted;
        }
        return toPermissionFlag(state);
    };

    return createUserMediaStatus({
        audio: deriveFlag('audio', permission.audio),
        video: deriveFlag('video', permission.video),
    });
};
